import React, { useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
  Skeleton,
  TablePagination,
} from "@mui/material";
import { alpha } from "@mui/material";
import { ClipboardList } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useGetList } from "../../../../hooks/useGetList";
import buildParams from "../../../../Utils/buildParams";
import {
  setLimit,
  setPage,
} from "../../../../redux/stockHistory/stockHistorySlice";

const logTypeColors = {
  sale: "error",
  initial: "info",
  "unit convert": "warning",
  purchase: "success",
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, "0");
  const hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 || 12;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(h12)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${ampm}`;
};

const capitalizeWords = (str) => {
  if (!str) return "-";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const columns = [
  { id: "dateTime", label: "Date & Time", minWidth: 170 },
  { id: "productItem", label: "Product Item", minWidth: 180 },
  { id: "itemUnit", label: "Item Unit", minWidth: 100 },
  { id: "batch", label: "Batch", minWidth: 100 },
  { id: "fromQty", label: "From Qty", minWidth: 100, align: "right" },
  { id: "changeQty", label: "Change Qty", minWidth: 110, align: "right" },
  { id: "toQty", label: "To Qty", minWidth: 100, align: "right" },
  { id: "logType", label: "Log Type", minWidth: 120, align: "center" },
  { id: "branch", label: "Branch", minWidth: 120 },
];

const skeletonCell = (col, key) => (
  <TableCell key={key} align={col.align || "left"}>
    <Skeleton
      variant="text"
      width={
        col.id === "dateTime"
          ? 120
          : col.id === "productItem"
            ? 140
            : col.id === "logType"
              ? 80
              : 60
      }
      height={col.id === "logType" ? 28 : 18}
      sx={col.id === "logType" ? { borderRadius: 1 } : undefined}
    />
  </TableCell>
);

const TableSkeleton = () => (
  <TableContainer sx={{ maxHeight: 600 }}>
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col.id}
              align={col.align || "left"}
              sx={{
                minWidth: col.minWidth,
                fontWeight: 700,
                fontSize: "0.8rem",
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                backgroundColor: "#f8fafc",
                borderBottom: "2px solid",
                borderColor: "divider",
                py: 2,
              }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(6)].map((_, rowIdx) => (
          <TableRow key={rowIdx}>
            {columns.map((col) => skeletonCell(col, `${rowIdx}-${col.id}`))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 10,
      px: 2,
    }}
  >
    <ClipboardList
      size={56}
      strokeWidth={1.2}
      style={{ color: "#cbd5e1", marginBottom: 16 }}
    />
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No Stock History Found
    </Typography>
    <Typography
      variant="body2"
      color="text.disabled"
      sx={{ maxWidth: 400, textAlign: "center" }}
    >
      There are no stock history records matching your current filters. Try
      adjusting the search criteria or date range.
    </Typography>
  </Box>
);

const StockHistoryList = () => {
  const filter = useSelector((state) => state.filterStockHistory);
  const param = buildParams(filter);

  const dispatch = useDispatch();

  const {
    data: logLists,
    loading: listLoading,
    fetchData,
  } = useGetList(
    `logs-v2`,
    true,
    Object.keys(param).length > 0 ? param : undefined,
  );

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };
  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(e.target.value));
    dispatch(setPage(1));
  };

  const rows = useMemo(
    () =>
      (logLists?.data || []).map((item) => ({
        id: item._id,
        dateTime: formatDateTime(item.createdAt),
        productItem: item.relatedItem?.medicineItemName || "-",
        itemUnit: item.relatedUnit?.name || "-",
        batch: item.relatedStockBatch?.code || "-",
        fromQty: item.qtyBefore ?? 0,
        changeQty: item.qtyChanged ?? 0,
        toQty: item.qtyAfter ?? 0,
        logType: capitalizeWords(item.logType),
        branch: item.relatedBranch?.name || "-",
      })),
    [logLists?.data],
  );

  const data = listLoading ? [] : rows;
  const isEmpty = !listLoading && data.length === 0;
  const totalCount = logLists?.pagination?.total || 0;

  useEffect(() => {
    fetchData(Object.keys(param).length > 0 ? param : undefined);
  }, [filter]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {listLoading ? (
        <TableSkeleton />
      ) : isEmpty ? (
        <EmptyState />
      ) : (
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    sx={{
                      minWidth: col.minWidth,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      backgroundColor: "#f8fafc",
                      borderBottom: "2px solid",
                      borderColor: "divider",
                      py: 2,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => {
                const changeColor =
                  row.changeQty > 0
                    ? "success.main"
                    : row.changeQty < 0
                      ? "error.main"
                      : "text.secondary";
                const changeSign = row.changeQty > 0 ? "+" : "";

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.primary.main, 0.04),
                      },
                      transition: "background-color 0.2s ease",
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" fontWeight={500}>
                          {row.dateTime.split(" ")[0]}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {row.dateTime.split(" ").slice(1).join(" ")}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.productItem}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.itemUnit}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                        }}
                      >
                        {row.batch}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {row.fromQty.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={changeColor}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {changeSign}
                        {row.changeQty.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {row.toQty.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.logType}
                        color={
                          logTypeColors[row.logType.toLowerCase()] || "default"
                        }
                        size="small"
                        variant="filled"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          minWidth: 80,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.branch}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {logLists?.data?.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={filter.limit}
          page={filter.page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            "& .MuiTablePagination-toolbar": {
              display: "flex",
              alignItems: "center",
              minHeight: "52px",
            },
            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      )}
    </Paper>
  );
};

export default StockHistoryList;
